import { createAction, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import _flatten from 'lodash/flatten';
import _map from 'lodash/map';

const actionsPrefix = 'app-job-search/settings';

export const INITIAL_STATE = {
  searchUrl: null,
  searchDetails: null,
  hrefLangs: [],
  endpoints: null,
  apiVersion: 'v2',
  isMobile: null,
  featureMap: null,
  mapsApiKey: null,
  isAuthenticated: false,
  cookies: null,
  url: null,
  fullUrl: null,
  email: null,
  base: {
    pathname: null,
    qs: null
  },
  savedSearches: [],
  hotScriptSrc: null,
  hotScriptSrcCss: null,
  refdata: {},
  searchType: '',
  employerId: false,
  templatesByOrg: {}
};

// setters (actions)
export const setSearchUrl = createAction(`${actionsPrefix}/SET_SEARCH_URL`);
export const setSearchDetails = createAction(`${actionsPrefix}/SET_SEARCH_DETAILS`);
export const setHrefLangs = createAction(`${actionsPrefix}/SET_HREF_LANGS`);
export const setEndpoints = createAction(`${actionsPrefix}/SET_ENDPOINTS`);
export const setIsMobile = createAction(`${actionsPrefix}/SET_IS_MOBILE`);
export const setFeatureMap = createAction(`${actionsPrefix}/SET_FEATURE_MAP`);
export const setMapsApiKey = createAction(`${actionsPrefix}/SET_MAPS_API_KEY`);
export const setIsAuthenticated = createAction(`${actionsPrefix}/SET_IS_AUTHENTICATED`);
export const setCookies = createAction(`${actionsPrefix}/SET_COOKIES`);
export const setBase = createAction(`${actionsPrefix}/SET_BASE`);
export const setUrl = createAction(`${actionsPrefix}/SET_URL`);
export const setFullUrl = createAction(`${actionsPrefix}/SET_FULL_URL`);
export const setPlainUrl = createAction(`${actionsPrefix}/SET_PLAIN_URL`);
export const setSavedSearches = createAction(`${actionsPrefix}/SET_SAVED_SEARCHES`);
export const setInternationalUserProfile = createAction(`${actionsPrefix}/SET_INT_USER_PROFILE`);
export const setAll = createAction(`${actionsPrefix}/SET_ALL`);

// reducers state => newState
export default handleActions({
  [setSearchUrl]: (state, { payload: searchUrl }) => ({ ...state, searchUrl }),
  [setSearchDetails]: (state, { payload: searchDetails }) => ({ ...state, searchDetails }),
  [setHrefLangs]: (state, { payload: hrefLangs }) => ({ ...state, hrefLangs }),
  [setEndpoints]: (state, { payload: endpoints }) => ({ ...state, endpoints }),
  [setIsMobile]: (state, { payload: isMobile }) => ({ ...state, isMobile }),
  [setFeatureMap]: (state, { payload: featureMap }) => ({ ...state, featureMap }),
  [setMapsApiKey]: (state, { payload: mapsApiKey }) => ({ ...state, mapsApiKey }),
  [setIsAuthenticated]: (state, { payload: isAuthenticated }) => ({ ...state, isAuthenticated }),
  [setCookies]: (state, { payload: cookies }) => ({ ...state, cookies }),
  [setBase]: (state, { payload: base }) => ({ ...state, base }),
  [setUrl]: (state, { payload: url }) => ({ ...state, url }),
  [setPlainUrl]: (state, { payload: url }) => ({ ...state, url }),
  [setFullUrl]: (state, { payload: url }) => ({ ...state, fullUrl: url }),
  [setSavedSearches]: (state, { payload: savedSearches }) => ({ ...state, savedSearches }),
  [setInternationalUserProfile]: (state, { payload: internationalUserProfile }) => ({ ...state, internationalUserProfile }),
  [setAll]: (state, { payload: {
    searchUrl,
    searchDetails,
    url,
    hrefLangs,
    endpoints,
    isMobile,
    featureMap,
    mapsApiKey,
    isAuthenticated,
    cookies,
    seoKey,
    fullUrl,
    email,
    hotScriptSrc,
    hotScriptSrcCss,
    refdata,
    employerId,
    searchType,
    templatesByOrg,
    internationalUserProfile
   }}) => ({
     ...state,
     searchUrl,
     searchDetails,
     url,
     hrefLangs,
     endpoints,
     isMobile,
     featureMap,
     mapsApiKey,
     isAuthenticated,
     cookies,
     seoKey,
     fullUrl,
     email,
     hotScriptSrc,
     hotScriptSrcCss,
     refdata,
     employerId,
     searchType,
     templatesByOrg,
     internationalUserProfile
   })
}, INITIAL_STATE);

// getters (selectors)
export const get = state => state.settings;
export const getSearchUrl = createSelector(get, ({ searchUrl }) => searchUrl);
export const getSearchDetails = createSelector(get, ({ searchDetails }) => {
  const hasSort = !!searchDetails.sort;
  const removedEmptyValusDetails = Object.keys(searchDetails).reduce((obj, key) => {
    let value = searchDetails[key];
    if ((Array.isArray(value) && value.length > 0) || (typeof value === 'string' && value.trim().length > 0)) {
      obj[key] = value;
      if (key === 'keywords') {
        if (Array.isArray(value)) value = value.join(' ');
        if (value.trim().length <= 2) {
          delete obj[key];
        }
      }
    }
    return obj;
  }, {});
  return hasSort ? removedEmptyValusDetails : {
    ...removedEmptyValusDetails,
    sort: 'date'
  };
});
export const getHrefLangs = createSelector(get, ({ hrefLangs }) => hrefLangs);
export const getEndpoints = createSelector(get, ({ endpoints }) => endpoints);
export const getApiVersion = createSelector(get, ({ apiVersion }) => apiVersion);
export const getIsMobile = createSelector(get, ({ isMobile }) => isMobile);
export const getFeatureMap = createSelector(get, ({ featureMap }) => featureMap);
export const getMapsApiKey = createSelector(get, ({ mapsApiKey }) => mapsApiKey);
export const getIsAuthenticated = createSelector(get, ({ isAuthenticated }) => isAuthenticated);
export const getCookies = createSelector(get, ({ cookies }) => cookies);
export const getUrl = createSelector(get, ({ url }) => url);
export const getInternationalUserProfile = createSelector(get, ({ internationalUserProfile }) => internationalUserProfile);
export const getRefdata = createSelector(get, ({ refdata }) => {
  return _flatten(_map(refdata, (o, key) => {
    o.map( v => v.type = key);
    return o;
  }));
});
export const getBase = createSelector(get, ({ base }) => base);
export const getSeoKey = createSelector(get, ({ seoKey }) => seoKey);
export const getFullUrl = createSelector(get, ({ fullUrl }) => fullUrl);
export const getSavedSearches = createSelector(get, ({ savedSearches }) => savedSearches);
export const getHotScriptSrc = createSelector(get, ({ hotScriptSrc }) => hotScriptSrc);
export const getHotScriptSrcCss = createSelector(get, ({ hotScriptSrcCss }) => hotScriptSrcCss);
export const getEmail = createSelector(get, ({ email }) => email);
export const getSearchType = createSelector(get, ({ searchType }) => searchType);
export const getTemplatesByOrg = createSelector(get, ({ templatesByOrg }) => templatesByOrg);
export const getEmployerId = createSelector(get, ({ employerId }) => employerId);
